import React, { useEffect, useState } from 'react';
import { Box, Grid, FormControl, Autocomplete, TextField, Typography, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { AppRoutes } from '../helper/appRoutes';
import { ClientFilingDefaultsPanel } from '../components/Elements/ClientFilingDefaultsPanel';

/**
 * Client Filing Defaults — the MISC-side admin screen for the Batch Filing Workflow checklist #8.
 * Pick a client, then set the values that never vary for them (carrier + coverage-per-state + agency
 * license for the special-license states) so filing can bypass extraction. Defaults are shown with an
 * override + revert. This is the standalone "tab"; the same editor also appears as a per-client pop-out
 * on the Batch Review screen. The defaults are per client and do NOT carry across clients (Kelly, 2026-07-14).
 */
interface PartnerOption {
    partnersId: number;
    customerName: string;
}

export function ClientFilingDefaults() {
    const { user } = useAuth();
    const token = user?.token;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Client/state the filer came from on the Batch Review screen — preselect the client here and hand
    // both back on "Back to Batch Filing Review" so they land on the same batch.
    const incomingPartnersId = searchParams.get('partnersId');
    const incomingStateId = searchParams.get('stateId');
    const [partners, setPartners] = useState<PartnerOption[]>([]);
    const [selectedPartner, setSelectedPartner] = useState<PartnerOption | null>(null);

    useEffect(() => {
        if (!token) return;
        apiClient('/Partners/getPartners', { headers: { Authorization: `Bearer ${token}` } }).then((res: any) => {
            if (Array.isArray(res?.data)) setPartners(res.data);
        });
    }, [token]);

    // Preselect the client the filer came from, once the partner list loads.
    useEffect(() => {
        if (!incomingPartnersId || selectedPartner || partners.length === 0) return;
        const p = partners.find((x) => String(x.partnersId) === incomingPartnersId);
        if (p) setSelectedPartner(p);
    }, [incomingPartnersId, partners, selectedPartner]);

    const backToBatch = () => {
        const params = new URLSearchParams();
        const pid = selectedPartner?.partnersId ?? incomingPartnersId;
        if (pid) params.set('partnersId', String(pid));
        if (incomingStateId) params.set('stateId', incomingStateId);
        const qs = params.toString();
        navigate(`${AppRoutes.filingQueue.batchReview}${qs ? `?${qs}` : ''}`);
    };

    return (
        <Box>
            <Button startIcon={<ArrowBack />}
                onClick={backToBatch}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#2099d2', mb: 1, pl: 0,
                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
            >Back to Batch Filing Review</Button>

            <Box
                sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, px: 2, py: 1.5,
                    borderBottom: '1px solid #e2e8f0', backgroundColor: '#fafbfc', minHeight: 56,
                    marginLeft: { xs: '-24px', md: '0px' }, marginRight: { xs: '-24px', md: '0px' },
                }}
            >
                <h4 className="mb-0 py-2" style={{ fontWeight: 600, color: '#0f172a' }}>
                    Client Filing Defaults
                    {selectedPartner?.customerName ? ` — ${selectedPartner.customerName}` : ''}
                </h4>
            </Box>

            <Box sx={{
                width: '100%', borderRadius: '16px', marginTop: '15px',
                border: '1px solid rgba(22, 46, 60, 0.10)', backgroundColor: '#fff',
                boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)',
                p: { xs: 1.5, sm: 2.5 },
            }}>
                <Grid container spacing={2} sx={{ mb: 2.5 }}>
                    <Grid item xl={3} lg={4} sm={6} xs={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                options={partners}
                                getOptionLabel={(o) => o.customerName}
                                isOptionEqualToValue={(a, b) => a.partnersId === b.partnersId}
                                value={selectedPartner}
                                onChange={(_e, value) => setSelectedPartner(value)}
                                renderInput={(params) => <TextField {...params} label="Client" size="small" />}
                            />
                        </FormControl>
                    </Grid>
                </Grid>

                {selectedPartner ? (
                    <ClientFilingDefaultsPanel
                        coverageOnly
                        token={token}
                        partnersId={selectedPartner.partnersId}
                        partnerName={selectedPartner.customerName}
                    />
                ) : (
                    <Box sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                        <Typography>Select a client to view and edit their filing defaults.</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default ClientFilingDefaults;
